import React, { memo, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, Zap, TrendingUp, Eye, Star, X, ChevronRight, Sparkles, Clock, Users } from 'lucide-react';
import axios from '../lib/axios';
import { Dialog, DialogContent } from './ui/dialog';
import { API } from '../App';

/**
 * BoostListingModal with A/B Testing
 * 
 * Shown immediately after a user publishes a new listing.
 * Supports 3 A/B test variants:
 * - A (Control): Standard "Boost My Listing" CTA
 * - B (Urgency): "Boost Now - Limited Time" with discount badge
 * - C (Social Proof): "Join 500+ Successful Sellers" with testimonial
 * 
 * Analytics tracking includes variant_id for conversion analysis.
 */
const BoostListingModal = memo(({ isOpen, onClose, listingId, listingTitle }) => {
  const navigate = useNavigate();
  const hasTrackedView = useRef(false);
  const [variant, setVariant] = useState(null);
  const [loadingVariant, setLoadingVariant] = useState(true);

  // Fetch A/B test variant when modal opens
  useEffect(() => {
    if (isOpen && !variant) {
      fetchVariant();
    }
  }, [isOpen]);

  const fetchVariant = async () => {
    try {
      setLoadingVariant(true);
      const response = await axios.get(`${API}/boost-popup/variant`);
      setVariant(response.data);
    } catch (error) {
      // Fallback to control variant
      setVariant({
        variant_id: 'A',
        name: 'Control',
        cta_text: 'Boost My Listing',
        subtitle: 'Get more eyes on your listing',
        discount: 0,
        badge: null
      });
    } finally {
      setLoadingVariant(false);
    }
  };

  // Track popup view when modal opens and variant is loaded
  useEffect(() => {
    if (isOpen && listingId && variant && !hasTrackedView.current) {
      hasTrackedView.current = true;
      trackEvent('popup_view');
    }
  }, [isOpen, listingId, variant]);

  // Reset tracking when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasTrackedView.current = false;
    }
  }, [isOpen]);

  const trackEvent = async (eventType) => {
    try {
      await axios.post(`${API}/analytics/boost-popup`, {
        event_type: eventType,
        listing_id: listingId,
        listing_title: listingTitle,
        variant_id: variant?.variant_id || 'A'
      });
    } catch (error) {
      console.debug('Boost analytics tracking error:', error);
    }
  };

  const handleBoostClick = () => {
    trackEvent('boost_click');
    // Pass discount info to promote page if applicable
    const discountParam = variant?.discount > 0 ? `?discount=${variant.discount}` : '';
    navigate(`/promote/${listingId}${discountParam}`);
    onClose();
  };

  const handleMaybeLater = () => {
    trackEvent('dismiss_click');
    onClose();
  };

  const handleCloseClick = () => {
    trackEvent('dismiss_click');
    onClose();
  };

  // Render variant-specific content
  const renderVariantContent = () => {
    if (!variant) return null;

    switch (variant.variant_id) {
      case 'B': // Urgency variant
        return (
          <>
            {/* Urgency badge */}
            {variant.badge && (
              <div className="absolute top-16 left-1/2 -translate-x-1/2 px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full animate-pulse shadow-lg">
                {variant.badge}
              </div>
            )}
            
            <p className="text-white/80 text-sm sm:text-base mb-4 max-w-xs mx-auto">
              {variant.subtitle}
            </p>

            {/* Discount banner */}
            {variant.discount > 0 && (
              <div className="bg-orange-500/20 border border-orange-400/30 rounded-lg p-3 mb-4 flex items-center justify-center gap-2">
                <Clock className="w-4 h-4 text-orange-300" />
                <span className="text-orange-200 font-semibold text-sm">
                  Save {variant.discount}% - Offer expires soon!
                </span>
              </div>
            )}

            {/* Benefits */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 text-left">
              <div className="flex items-center gap-2 mb-3">
                <Rocket className="w-5 h-5 text-orange-300" />
                <span className="font-semibold text-sm sm:text-base">Don't miss out:</span>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-white/90">
                  <Eye className="w-4 h-4 text-orange-300 flex-shrink-0" />
                  <span>Get <strong>10x more views</strong> instantly</span>
                </li>
                <li className="flex items-center gap-2 text-white/90">
                  <Star className="w-4 h-4 text-orange-300 flex-shrink-0" />
                  <span>Featured placement <strong>today</strong></span>
                </li>
                <li className="flex items-center gap-2 text-white/90">
                  <TrendingUp className="w-4 h-4 text-orange-300 flex-shrink-0" />
                  <span>Beat competitors to the top</span>
                </li>
              </ul>
            </div>
          </>
        );

      case 'C': // Social proof variant
        return (
          <>
            {/* Social proof badge */}
            {variant.badge && (
              <div className="absolute top-16 left-1/2 -translate-x-1/2 px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full shadow-lg">
                {variant.badge}
              </div>
            )}
            
            <p className="text-white/80 text-sm sm:text-base mb-4 max-w-xs mx-auto">
              {variant.subtitle}
            </p>

            {/* Testimonial */}
            <div className="bg-green-500/20 border border-green-400/30 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-green-300" />
                <span className="text-green-200 font-semibold text-sm">Seller Success Story</span>
              </div>
              <p className="text-white/80 text-xs italic">
                "Sold my car in just 2 days after boosting. Best investment ever!" - Maria K.
              </p>
            </div>

            {/* Benefits */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 text-left">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-green-300" />
                <span className="font-semibold text-sm sm:text-base">Join successful sellers:</span>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-white/90">
                  <Eye className="w-4 h-4 text-green-300 flex-shrink-0" />
                  <span><strong>500+</strong> sellers boosted this month</span>
                </li>
                <li className="flex items-center gap-2 text-white/90">
                  <Star className="w-4 h-4 text-green-300 flex-shrink-0" />
                  <span><strong>3x faster</strong> sales on average</span>
                </li>
                <li className="flex items-center gap-2 text-white/90">
                  <TrendingUp className="w-4 h-4 text-green-300 flex-shrink-0" />
                  <span><strong>94%</strong> satisfaction rate</span>
                </li>
              </ul>
            </div>
          </>
        );

      default: // Control variant (A)
        return (
          <>
            <p className="text-white/80 text-sm sm:text-base mb-6 max-w-xs mx-auto">
              Your listing is now live. Want to get more eyes on it?
            </p>

            {/* Benefits */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 text-left">
              <div className="flex items-center gap-2 mb-3">
                <Rocket className="w-5 h-5 text-yellow-300" />
                <span className="font-semibold text-sm sm:text-base">Boost your listing to:</span>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-white/90">
                  <Eye className="w-4 h-4 text-yellow-300 flex-shrink-0" />
                  <span>Get <strong>10x more views</strong></span>
                </li>
                <li className="flex items-center gap-2 text-white/90">
                  <Star className="w-4 h-4 text-yellow-300 flex-shrink-0" />
                  <span>Appear in <strong>Featured section</strong></span>
                </li>
                <li className="flex items-center gap-2 text-white/90">
                  <TrendingUp className="w-4 h-4 text-yellow-300 flex-shrink-0" />
                  <span>Rank <strong>higher in search</strong></span>
                </li>
              </ul>
            </div>
          </>
        );
    }
  };

  // Get CTA button styles based on variant
  const getCtaStyles = () => {
    switch (variant?.variant_id) {
      case 'B':
        return 'bg-orange-500 hover:bg-orange-400 shadow-orange-500/30 hover:shadow-orange-400/40';
      case 'C':
        return 'bg-green-500 hover:bg-green-400 shadow-green-500/30 hover:shadow-green-400/40';
      default:
        return 'bg-yellow-400 hover:bg-yellow-300 text-gray-900 shadow-yellow-400/30 hover:shadow-yellow-300/40';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCloseClick}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 bg-transparent shadow-none">
        <div className="relative bg-gradient-to-br from-primary via-blue-600 to-indigo-700 rounded-2xl overflow-hidden">
          {/* Background decorations */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-yellow-400/20 rounded-full blur-3xl" />
            <Sparkles className="absolute top-4 right-4 w-6 h-6 text-yellow-300/50 animate-pulse" />
            <Sparkles className="absolute bottom-20 left-4 w-4 h-4 text-white/30 animate-pulse delay-300" />
          </div>

          {/* Close button */}
          <button
            onClick={handleCloseClick}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors z-10"
            data-testid="boost-modal-close"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Content */}
          <div className="relative p-6 sm:p-8 text-center text-white">
            {/* Success checkmark */}
            <div className="mx-auto mb-4 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-green-500 flex items-center justify-center shadow-lg shadow-green-500/30">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            {/* Title */}
            <h2 className="text-xl sm:text-2xl font-bold mb-2">
              Listing Published!
            </h2>

            {/* Variant-specific content */}
            {loadingVariant ? (
              <div className="h-48 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              </div>
            ) : (
              renderVariantContent()
            )}

            {/* CTA Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleBoostClick}
                disabled={loadingVariant}
                className={`w-full py-3.5 px-6 font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 ${
                  variant?.variant_id === 'B' || variant?.variant_id === 'C' ? 'text-white' : 'text-gray-900'
                } ${getCtaStyles()}`}
                data-testid="boost-listing-btn"
              >
                <Zap className="w-5 h-5" />
                <span>{variant?.cta_text || 'Boost My Listing'}</span>
                <ChevronRight className="w-5 h-5" />
              </button>
              
              <button
                onClick={handleMaybeLater}
                className="w-full py-3 px-6 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all"
                data-testid="boost-modal-later"
              >
                Maybe Later
              </button>
            </div>

            {/* Bottom note */}
            <p className="mt-4 text-xs text-white/50">
              You can always boost your listing later from your dashboard
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});

BoostListingModal.displayName = 'BoostListingModal';

export default BoostListingModal;
